'use client';

import React, { useEffect, useState, useCallback } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { fetchSalesTrend } from '@/lib/supabase/dashboardService';
import { createClient } from '@/lib/supabase/client';

function formatRevenue(val: number) {
  if (val >= 10000000) return `₹${(val / 10000000).toFixed(1)}Cr`;
  if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
  return `₹${(val / 1000).toFixed(0)}K`;
}

interface TooltipProps {
  active?: boolean;
  payload?: Array<{ value: number; name: string }>;
  label?: string;
}

function CustomTooltip({ active, payload, label }: TooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="bg-card border border-border rounded-xl shadow-dropdown px-3 py-2 text-xs">
      <p className="font-600 text-foreground mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={`tt-${i}`} className="text-muted-foreground">
          {p.name === 'revenue' ? (
            <><span className="font-600 text-primary">{formatRevenue(p.value)}</span> revenue</>
          ) : (
            <><span className="font-600 text-foreground">{p.value.toLocaleString('en-IN')}</span> units</>
          )}
        </p>
      ))}
    </div>
  );
}

export default function SalesTrendChart() {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(() => {
    fetchSalesTrend(30).then((result) => {
      if (result) setData(result);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    loadData();

    const supabase = createClient();
    const channel = supabase
      .channel('sales-trend-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, loadData)
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [loadData]);

  if (loading) return <div className="h-[220px] bg-muted/30 rounded-lg animate-pulse" />;
  if (!data.length) return <div className="h-[220px] flex items-center justify-center text-xs text-muted-foreground">No sales data available</div>;

  return (
    <ResponsiveContainer width="100%" height={220}>
      <AreaChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="var(--primary)" stopOpacity={0.18} />
            <stop offset="95%" stopColor="var(--primary)" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
        <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }} tickLine={false} axisLine={false} interval={4} />
        <YAxis tickFormatter={formatRevenue} tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }} tickLine={false} axisLine={false} width={52} />
        <Tooltip content={<CustomTooltip />} />
        <Area type="monotone" dataKey="revenue" stroke="var(--primary)" strokeWidth={2} fill="url(#revenueGrad)" dot={false} activeDot={{ r: 4, fill: 'var(--primary)', strokeWidth: 0 }} />
      </AreaChart>
    </ResponsiveContainer>
  );
}